import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
  Radio
} from '@mui/material';
import { Phone, Dialpad } from '@mui/icons-material';

/**
 * Modal para seleccionar el skill con el que se realizará la llamada
 * desde el seguimiento o historial
 */
const SkillSelectorModal = ({ open, skills, phoneNumber, onSelect, onCancel, defaultSkill = null }) => {
  const [selectedSkill, setSelectedSkill] = useState(defaultSkill);

  const handleSelect = () => {
    if (selectedSkill) {
      onSelect(selectedSkill);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Selecciona el skill para la llamada
          </Typography>
        </Box>
        {phoneNumber && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Dialpad sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Número: <strong>{phoneNumber}</strong>
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {skills.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No tienes skills Manual o Preview disponibles
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {skills.map((skill) => (
              <ListItem
                key={skill.id || skill.skillname}
                disablePadding
                sx={{ mb: 0.5 }}
              >
                <ListItemButton
                  selected={selectedSkill?.skillname === skill.skillname}
                  onClick={() => setSelectedSkill(skill)}
                  sx={{
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: selectedSkill?.skillname === skill.skillname ? 'primary.main' : 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      '&:hover': {
                        backgroundColor: 'primary.light'
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Radio
                      checked={selectedSkill?.skillname === skill.skillname}
                      value={skill.skillname}
                      size="small"
                    />
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {skill.skillname}
                        </Typography>
                        <Chip
                          label={skill.dialing_mode || 'Manual'}
                          size="small"
                          color={skill.dialing_mode === 'Preview' ? 'secondary' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {skill.skilldef && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Def: {skill.skilldef}
                          </Typography>
                        )}
                        {skill.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {skill.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {selectedSkill && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'success.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.main'
            }}
          >
            <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
              Skill seleccionado:
            </Typography>
            <Typography variant="body2" color="success.dark" sx={{ mt: 0.5 }}>
              {selectedSkill.skillname}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSelect}
          disabled={!selectedSkill}
          variant="contained"
          startIcon={<Phone />}
        >
          Llamar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkillSelectorModal;
